import type {Extra} from 'src/internal/types';

export type Track<TValues extends {[key: string]: unknown}> = <
  TName extends keyof TValues,
>(
  name: TName,
  value: TValues[TName],
  extra: Extra,
) => void;

export default class Registry<TValues extends {[key: string]: unknown}> {
  private boundFlush: () => void;
  private boundTrack: Track<TValues>;
  private monitors: Set<() => void>;
  private storeByName: Partial<Record<keyof TValues, Map<symbol, unknown>>>;
  private storeBySymbol: Record<symbol, Set<keyof TValues>>;
  private timeout: ReturnType<typeof setTimeout> | null;

  constructor(track: Track<TValues>) {
    this.boundFlush = this.flush.bind(this);
    this.boundTrack = track.bind(this);
    this.monitors = new Set();
    this.storeByName = {};
    this.storeBySymbol = {};
    this.timeout = null;
  }

  monitor(callback: () => void) {
    this.monitors.add(callback);
  }

  unmonitor(callback: () => void) {
    this.monitors.delete(callback);
  }

  add<TName extends keyof TValues>(
    name: TName,
    value: TValues[TName],
    extra: Extra,
    symbol: symbol,
  ) {
    const storeByName = this.storeByName;
    const storeBySymbol = this.storeBySymbol;

    if (!storeByName.hasOwnProperty(name)) {
      storeByName[name] = new Map();
    }

    if (!storeBySymbol.hasOwnProperty(symbol)) {
      storeBySymbol[symbol] = new Set();
    }

    if (storeByName[name]?.get(symbol) !== value) {
      this.boundTrack(name, value, extra);
    }

    storeByName[name]?.set(symbol, value);
    storeBySymbol[symbol]?.add(name);

    this.queue();
  }

  delete(symbol: symbol) {
    const storeByName = this.storeByName;
    const storeBySymbol = this.storeBySymbol;

    storeBySymbol[symbol]?.forEach((name) => {
      storeByName[name]?.delete(symbol);

      if (!storeByName[name]?.size) {
        delete storeByName[name];
      }
    });

    delete storeBySymbol[symbol];

    this.queue();
  }

  keys(): (keyof TValues)[] {
    return Object.keys(this.storeByName).sort() as (keyof TValues)[];
  }

  private queue() {
    if (!this.timeout) {
      this.timeout = setTimeout(this.boundFlush, 500);
    }
  }

  private flush() {
    this.timeout = null;
    this.monitors.forEach((callback) => callback());
  }
}
